import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  LayoutDashboard,
  Loader2,
  LogOut,
  Save,
  Settings,
  Users,
} from "lucide-react";
import { memberApi } from "../api/member";
import AccountSecurityForms from "../Components/AccountSecurityForms";
import HouseholdEditor, {
  cleanHouseholdRows,
  householdSizeValue,
} from "../Components/HouseholdEditor";
import { pageRoutes } from "../utils";

const navItems = [
  { path: pageRoutes.AdminDashboard, label: "Dashboard", icon: LayoutDashboard },
  { path: pageRoutes.AdminMembers, label: "Members", icon: Users },
  { path: pageRoutes.AdminPayments, label: "Payments", icon: CreditCard },
  { path: pageRoutes.AdminSettings, label: "Settings", icon: Settings },
];
const adminRoles = new Set(["admin", "cashier", "super_admin"]);

function adminMemberPath(id) {
  return `/admin/members/${encodeURIComponent(id)}`;
}

function formatDate(value) {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatCurrency(amount, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format((Number(amount) || 0) / 100);
}

// Ledger rows carry donor fields; the member-detail payment history still sends
// raw member_payment rows, so fall back gracefully rather than showing blanks.
function donorLabel(payment) {
  const name = payment.donorName || payment.memberName;
  if (name) return name;
  const email = payment.donorEmail || payment.memberEmail;
  if (email) return email;
  if (payment.memberUserId || payment.userId) return "Member";
  return "Guest donor";
}

function donorTypeLabel(payment) {
  if (payment.donorType === "member") return "Member";
  if (payment.donorType === "guest") return "Guest donor";
  return payment.memberUserId || payment.userId ? "Member" : "Guest donor";
}

function ContributionStatusBadge({ payment }) {
  const status = String(payment.status || "unknown");
  const label = payment.statusLabel || status.replace(/_/g, " ");
  const tone =
    status === "paid"
      ? "bg-emerald-50 text-emerald-800"
      : status === "pending"
      ? "bg-amber-50 text-amber-900"
      : status === "failed"
      ? "bg-red-50 text-red-800"
      : "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${tone}`}>
      {label}
    </span>
  );
}

function StatusBadge({ value }) {
  const label = String(value || "unknown").replace(/_/g, " ");
  const good = ["active", "paid", "true"].includes(String(value));
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
        good ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
      }`}
    >
      {label}
    </span>
  );
}

function AdminShell({ user, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  async function logout() {
    await memberApi("/api/auth/logout", { method: "POST" });
    navigate(pageRoutes.AdminLogin, { replace: true });
  }
  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      <section className="bg-[#722F37] px-4 py-8 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#E7D6A8]">
              Admin Dashboard
            </p>
            <h1 className="mt-2 font-serif-elegant text-3xl font-bold">
              St. Arsema member administration
            </h1>
            <p className="mt-1 text-sm text-white/75">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 px-4 py-2 text-sm font-semibold hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </section>
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 lg:grid-cols-[220px_1fr]">
        <nav className="space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold ${
                  active
                    ? "bg-[#722F37] text-white"
                    : "bg-white text-gray-700 hover:bg-[#722F37]/10"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <main>{children}</main>
      </div>
    </div>
  );
}

function SummaryCards() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    memberApi("/api/admin/dashboard-summary").then(setSummary);
  }, []);
  if (!summary) {
    return <Loader2 className="h-6 w-6 animate-spin text-[#722F37]" />;
  }
  const cards = [
    ["Total members", summary.totalMembers],
    ["Pending registrations", summary.pendingRegistrations],
    ["Pending email verification", summary.pendingEmailVerification],
    ["Pending payment", summary.pendingPayment],
    ["Paid memberships", summary.paidMemberships],
    ["No immediate payment", summary.noImmediatePaymentRegistrations],
  ];
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-[#C9A227]/20 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
              {label}
            </p>
            <p className="mt-3 text-3xl font-bold text-[#722F37]">{value}</p>
          </div>
        ))}
      </div>
      <AdminTable title="Recent registrations" members={summary.recentRegistrations || []} />
      <PaymentsTable title="Recent payments & donations" payments={summary.recentPayments || []} />
    </div>
  );
}

function AdminTable({ title, members }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#C9A227]/20 bg-white">
      <div className="border-b px-5 py-4">
        <h2 className="font-serif-elegant text-xl font-bold text-[#722F37]">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] text-left text-sm">
          <thead className="border-b bg-[#fff8ec] text-xs uppercase tracking-[0.14em] text-gray-500">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Verified</th>
              <th className="px-4 py-3">Account</th>
              <th className="px-4 py-3">Membership</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Number of Household</th>
              <th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {members.map((member) => (
              <tr key={member.userId}>
                <td className="px-4 py-3">
                  <Link className="font-semibold text-[#722F37] underline" to={adminMemberPath(member.userId)}>
                    {[member.firstName, member.lastName].filter(Boolean).join(" ") || "Unnamed"}
                  </Link>
                </td>
                <td className="px-4 py-3">{member.email}</td>
                <td className="px-4 py-3"><StatusBadge value={String(member.emailVerified)} /></td>
                <td className="px-4 py-3"><StatusBadge value={member.accountStatus} /></td>
                <td className="px-4 py-3"><StatusBadge value={member.membershipStatus} /></td>
                <td className="px-4 py-3"><StatusBadge value={member.paymentStatus} /></td>
                <td className="px-4 py-3 font-semibold text-gray-800">{member.householdSize || 1}</td>
                <td className="px-4 py-3">{formatDate(member.createdAt)}</td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td className="px-4 py-6 text-gray-500" colSpan="8">No records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MembersPage() {
  const [filters, setFilters] = useState({ search: "", membershipStatus: "", paymentStatus: "" });
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
    memberApi(`/api/admin/members?${params}`).then(setData);
  }, [filters]);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-xl border border-[#C9A227]/20 bg-white p-4 md:grid-cols-3">
        <input
          value={filters.search}
          onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder="Search name, email, or phone"
          className="rounded-xl border border-gray-300 px-4 py-3"
        />
        <select
          value={filters.membershipStatus}
          onChange={(event) => setFilters((current) => ({ ...current, membershipStatus: event.target.value }))}
          className="rounded-xl border border-gray-300 px-4 py-3"
        >
          <option value="">All membership statuses</option>
          <option value="pending_review">Pending review</option>
          <option value="pending_payment">Pending payment</option>
          <option value="active">Approved (active member)</option>
        </select>
        <select
          value={filters.paymentStatus}
          onChange={(event) => setFilters((current) => ({ ...current, paymentStatus: event.target.value }))}
          className="rounded-xl border border-gray-300 px-4 py-3"
        >
          <option value="">All payment statuses</option>
          <option value="not_required">Not required</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
        </select>
      </div>
      {data ? <AdminTable title="Members" members={data.members || []} /> : <Loader2 className="h-6 w-6 animate-spin" />}
    </div>
  );
}

function MemberDetailPage() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [contribution, setContribution] = useState(null);
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("idle");
  const [saveError, setSaveError] = useState("");
  const [householdForm, setHouseholdForm] = useState({
    householdSize: "1",
    householdMembers: [],
  });
  const [householdStatus, setHouseholdStatus] = useState("idle");

  async function load() {
    const payload = await memberApi(`/api/admin/members/${encodeURIComponent(id)}`);
    setDetail(payload.member);
    setContribution(payload.contributionSummary || null);
    setForm({
      membershipStatus: payload.member.membership?.membershipStatus || "",
      accountStatus: payload.member.user?.accountStatus || "",
      internalAdminNotes: payload.member.profile?.internalAdminNotes || "",
    });
    setHouseholdForm({
      householdSize: householdSizeValue(
        payload.member.profile?.householdSize,
        payload.member.householdMembers?.length || 0
      ),
      householdMembers: payload.member.householdMembers || [],
    });
  }
  useEffect(() => { load(); }, [id]);

  // Only send enum fields that actually have a value; the backend rejects blanks.
  function reviewPayload(overrides = {}) {
    const next = { ...form, ...overrides };
    const payload = { internalAdminNotes: next.internalAdminNotes || "" };
    if (next.membershipStatus) payload.membershipStatus = next.membershipStatus;
    if (next.accountStatus) payload.accountStatus = next.accountStatus;
    return payload;
  }

  async function submitReview(overrides, confirmMessage) {
    if (!window.confirm(confirmMessage)) return;
    setStatus("loading");
    setSaveError("");
    let succeeded = false;
    try {
      await memberApi(`/api/admin/members/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(reviewPayload(overrides)),
      });
      succeeded = true;
      await load();
    } catch (error) {
      setSaveError(
        succeeded
          ? "Saved, but this page could not be refreshed. Reload to see the latest values."
          : error?.message || "We could not save these changes. Please try again."
      );
    } finally {
      // Never leave the button stuck on "Saving...", whatever failed above.
      setStatus(succeeded ? "saved" : "error");
    }
  }

  async function save(event) {
    event.preventDefault();
    await submitReview({}, "Save admin changes for this member?");
  }

  async function approveMembership() {
    await submitReview(
      {
        membershipStatus: "active",
        accountStatus: form.accountStatus === "disabled" ? "disabled" : "active",
      },
      "Approve this membership? The member will no longer appear under Pending registrations."
    );
  }

  async function saveHousehold(event) {
    event.preventDefault();
    if (!window.confirm("Save household changes for this member?")) return;
    setHouseholdStatus("loading");
    try {
      const householdMembers = cleanHouseholdRows(householdForm.householdMembers);
      const payload = await memberApi(`/api/admin/members/${encodeURIComponent(id)}/household`, {
        method: "PATCH",
        body: JSON.stringify({
          householdSize: householdForm.householdSize,
          householdMembers,
        }),
      });
      const member = payload.member;
      setDetail(member);
      setHouseholdForm({
        householdSize: householdSizeValue(
          member.profile?.householdSize,
          member.householdMembers?.length || 0
        ),
        householdMembers: member.householdMembers || [],
      });
      setHouseholdStatus("saved");
    } catch (error) {
      setHouseholdStatus("error");
    }
  }

  if (!detail) return <Loader2 className="h-6 w-6 animate-spin text-[#722F37]" />;
  const { user, profile, membership, payments, auditLogs } = detail;
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-[#C9A227]/20 bg-white p-5">
        <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
          {[profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || user.email}
        </h2>
        <dl className="mt-4 grid gap-4 md:grid-cols-3">
          <div><dt className="text-sm text-gray-500">Email</dt><dd className="font-semibold">{user.email}</dd></div>
          <div><dt className="text-sm text-gray-500">Verified</dt><dd><StatusBadge value={String(user.emailVerified)} /></dd></div>
          <div><dt className="text-sm text-gray-500">Account</dt><dd><StatusBadge value={user.accountStatus} /></dd></div>
          <div><dt className="text-sm text-gray-500">Membership</dt><dd><StatusBadge value={membership?.membershipStatus} /></dd></div>
          <div><dt className="text-sm text-gray-500">Phone</dt><dd>{profile?.phone || "Not provided"}</dd></div>
          <div><dt className="text-sm text-gray-500">Household size</dt><dd>{profile?.householdSize || 1}</dd></div>
        </dl>
      </div>

      <ContributionSummaryCard summary={contribution} />

      <form onSubmit={save} className="rounded-xl border border-[#C9A227]/20 bg-white p-5">
        <h3 className="font-serif-elegant text-xl font-bold text-[#722F37]">Admin review</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label>
            <span className="mb-2 block text-sm font-medium">Membership review status</span>
            <select className="w-full rounded-xl border px-4 py-3" value={form.membershipStatus} onChange={(event) => setForm((current) => ({ ...current, membershipStatus: event.target.value }))}>
              <option value="pending_review">Pending review</option>
              <option value="pending_payment">Pending payment</option>
              <option value="active">Approved (active member)</option>
              <option value="failed">Failed</option>
              <option value="canceled">Canceled</option>
            </select>
            <span className="mt-2 block text-xs text-gray-500">
              Approving membership removes this person from Pending registrations.
            </span>
          </label>
          <label>
            <span className="mb-2 block text-sm font-medium">Account login status</span>
            <select className="w-full rounded-xl border px-4 py-3" value={form.accountStatus} onChange={(event) => setForm((current) => ({ ...current, accountStatus: event.target.value }))}>
              <option value="active">Active (can sign in)</option>
              <option value="disabled">Disabled (cannot sign in)</option>
              <option value="pending_email_verification">Pending email verification</option>
            </select>
            <span className="mt-2 block text-xs text-gray-500">
              Controls sign-in access only. This does not approve the membership.
            </span>
          </label>
          <label className="md:col-span-2">
            <span className="mb-2 block text-sm font-medium">Internal admin notes</span>
            <textarea className="min-h-28 w-full rounded-xl border px-4 py-3" value={form.internalAdminNotes} onChange={(event) => setForm((current) => ({ ...current, internalAdminNotes: event.target.value }))} />
          </label>
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white">
            <Save className="h-4 w-4" />
            {status === "loading" ? "Saving..." : status === "saved" ? "Saved" : "Save changes"}
          </button>
          {form.membershipStatus !== "active" && (
            <button
              type="button"
              onClick={approveMembership}
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-3 font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-60"
            >
              <CheckCircle2 className="h-4 w-4" />
              Approve membership
            </button>
          )}
        </div>
        {saveError && (
          <p className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
            <span>{saveError}</span>
          </p>
        )}
      </form>

      <HouseholdEditor
        title="Household members"
        helperText="Admin household edits are saved to this member profile and audit logged."
        householdSize={householdForm.householdSize}
        householdMembers={householdForm.householdMembers}
        onHouseholdSizeChange={(householdSize) =>
          setHouseholdForm((current) => ({ ...current, householdSize }))
        }
        onHouseholdMembersChange={(householdMembers) =>
          setHouseholdForm((current) => ({ ...current, householdMembers }))
        }
        onSubmit={saveHousehold}
        saveStatus={householdStatus}
        saveLabel="Save household changes"
        savingLabel="Saving household changes..."
        savedButtonLabel="Household changes saved"
        successText="Household information saved."
        errorText="We could not save household information. Please try again."
      />

      <PaymentsTable title="Payment history" payments={payments || []} />
      <div className="rounded-xl border border-[#C9A227]/20 bg-white p-5">
        <h3 className="font-serif-elegant text-xl font-bold text-[#722F37]">Audit history</h3>
        <div className="mt-4 space-y-2 text-sm">
          {(auditLogs || []).map((log) => (
            <div key={log.id} className="rounded-lg bg-[#fff8ec] px-3 py-2">
              <span className="font-semibold">{log.action}</span> · {formatDate(log.createdAt)}
            </div>
          ))}
          {(!auditLogs || auditLogs.length === 0) && <p className="text-gray-500">No audit history yet.</p>}
        </div>
      </div>
    </div>
  );
}

// Current standing (derived from Stripe-confirmed records) is deliberately kept
// separate from, and shown above, the historical registration choice. Showing
// "not required" beside an active recurring contribution without that framing
// is what made this screen read as a contradiction.
function ContributionSummaryCard({ summary }) {
  const contribution = summary?.contribution || summary?.payment || null;
  const registration = summary?.registrationPaymentChoice || null;
  return (
    <div className="rounded-xl border border-[#C9A227]/20 bg-white p-5">
      <h3 className="font-serif-elegant text-xl font-bold text-[#722F37]">
        Contribution summary
      </h3>
      <dl className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <dt className="text-sm text-gray-500">Current contribution status</dt>
          <dd className="font-semibold text-gray-900">
            {contribution?.statusLabel || "Not available"}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-gray-500">Contribution type</dt>
          <dd className="font-semibold text-gray-900">
            {contribution?.typeLabel || "Not available"}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-gray-500">Latest Stripe-confirmed contribution</dt>
          <dd className="font-semibold text-gray-900">
            {contribution?.latestPaidAt
              ? `${formatCurrency(contribution.latestAmount, contribution.currency)} on ${formatDate(contribution.latestPaidAt)}`
              : "None recorded"}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-gray-500">Total Stripe-confirmed contributions</dt>
          <dd className="font-semibold text-gray-900">
            {formatCurrency(
              contribution?.totalAmount ?? contribution?.totalPaid ?? 0,
              contribution?.currency
            )}
            {contribution?.paymentCount
              ? ` across ${contribution.paymentCount} payment${
                  contribution.paymentCount === 1 ? "" : "s"
                }`
              : ""}
          </dd>
        </div>
      </dl>

      <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-sm text-gray-500">Registration payment choice</p>
        <p className="mt-1 font-semibold text-gray-900">
          {registration?.label || "No registration payment choice recorded"}
        </p>
      </div>

      <p className="mt-3 text-sm text-gray-600">
        Registration payment choice reflects what the member selected when registering.
        Current contribution status is derived from Stripe-confirmed payments and may
        change later. Neither can be edited here.
      </p>

      {contribution?.incompleteAttemptCount > 0 && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
          <span>
            {contribution.incompleteAttemptCount} incomplete checkout attempt
            {contribution.incompleteAttemptCount === 1 ? "" : "s"} recorded. These are not
            counted as contributions.
          </span>
        </p>
      )}
    </div>
  );
}

// A pending row is a checkout session that was started and never paid, so it
// must never render as if money arrived.
function isCheckoutAttempt(payment) {
  return payment.isCheckoutAttempt === true || payment.status === "pending";
}

function completedAtLabel(payment) {
  if (payment.paidAt) return formatDate(payment.paidAt);
  return isCheckoutAttempt(payment) ? "Not completed" : "Not available";
}

function PaymentsTable({ title, payments, attemptView = false }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#C9A227]/20 bg-white">
      <div className="border-b px-5 py-4">
        <h2 className="font-serif-elegant text-xl font-bold text-[#722F37]">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1040px] text-left text-sm">
          <thead className="border-b bg-[#fff8ec] text-xs uppercase tracking-[0.14em] text-gray-500">
            <tr>
              <th className="px-4 py-3">Donor</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Purpose</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">{attemptView ? "Attempt status" : "Status"}</th>
              <th className="px-4 py-3">{attemptView ? "Started" : "Completed at"}</th>
              <th className="px-4 py-3">{attemptView ? "Stripe session" : "Stripe refs"}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {payments.map((payment) => (
              <tr key={payment.id} className={isCheckoutAttempt(payment) ? "bg-amber-50/60" : ""}>
                <td className="px-4 py-3">{donorLabel(payment)}</td>
                <td className="px-4 py-3">{payment.donorEmail || payment.memberEmail || "Unknown email"}</td>
                <td className="px-4 py-3">{donorTypeLabel(payment)}</td>
                <td className="px-4 py-3">{payment.purposeLabel || payment.purpose?.replace(/_/g, " ") || "Not available"}</td>
                <td className="px-4 py-3">{formatCurrency(payment.amount, payment.currency)}</td>
                <td className="px-4 py-3"><ContributionStatusBadge payment={payment} /></td>
                <td className="px-4 py-3">
                  {attemptView ? formatDate(payment.createdAt) : completedAtLabel(payment)}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  <div>{payment.stripeCheckoutSessionId || "no checkout session"}</div>
                  <div>{payment.stripePaymentIntentId || "no payment intent"}</div>
                  {payment.stripeSubscriptionId && <div>{payment.stripeSubscriptionId}</div>}
                  {payment.stripeReceiptUrl && (
                    <a className="font-semibold text-[#722F37] underline" href={payment.stripeReceiptUrl} target="_blank" rel="noopener noreferrer">Receipt</a>
                  )}
                </td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-gray-500" colSpan="8">
                  {attemptView
                    ? "No incomplete checkout attempts found."
                    : "No payments or donations found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUS_COUNT_NOUN = {
  paid: "paid",
  failed: "failed",
  canceled: "canceled",
};

// Pending rows are incomplete checkout attempts, not contributions, so the
// pending view is titled and framed as something other than a payments table.
function paymentsViewCopy(status, total) {
  const count = total === null || total === undefined ? "" : total;
  if (status === "pending") {
    return {
      title: `Incomplete checkout attempts${count === "" ? "" : ` (${count})`}`,
      tone: "warning",
      message:
        "These rows are not completed donations. They represent checkout sessions that were started but not paid. Stripe reconciliation confirms they are unpaid or abandoned.",
    };
  }
  if (status === "all") {
    return {
      title: "Payments & donations (all statuses)",
      tone: "warning",
      message:
        "Paid rows are completed contributions. Pending rows are incomplete checkout attempts and are not included in received donation totals.",
    };
  }
  if (status === "paid") {
    return {
      title: `Payments & donations${count === "" ? "" : ` (${count} paid)`}`,
      tone: "info",
      message: "These are completed Stripe payments recorded in the church ledger.",
    };
  }
  const noun = STATUS_COUNT_NOUN[status] || status;
  return {
    title: `Payments & donations${count === "" ? "" : ` (${count} ${noun})`}`,
    tone: "info",
    message: "",
  };
}

function PaymentsPage() {
  // Default to completed money so abandoned checkout attempts do not read as donations.
  const [filters, setFilters] = useState({ search: "", status: "paid", source: "" });
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
    memberApi(`/api/admin/payments?${params}`).then(setData);
  }, [filters]);
  const view = paymentsViewCopy(filters.status, data?.pagination?.total);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-xl border border-[#C9A227]/20 bg-white p-4 md:grid-cols-3">
        <input value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Search donor, email, or Stripe ref" className="rounded-xl border px-4 py-3" />
        <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} className="rounded-xl border px-4 py-3">
          <option value="paid">Paid / completed</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="canceled">Canceled</option>
          <option value="all">All statuses</option>
        </select>
        <select value={filters.source} onChange={(event) => setFilters((current) => ({ ...current, source: event.target.value }))} className="rounded-xl border px-4 py-3">
          <option value="">All sources</option>
          <option value="donation">Donations</option>
          <option value="member_payment">Membership payments</option>
        </select>
      </div>
      {view.message && (
        <p
          className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
            view.tone === "warning"
              ? "border-amber-200 bg-amber-50 text-amber-900"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          {view.tone === "warning" ? (
            <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
          ) : (
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
          )}
          <span>{view.message}</span>
        </p>
      )}
      {data ? (
        <PaymentsTable
          title={view.title}
          attemptView={filters.status === "pending"}
          payments={data.payments || []}
        />
      ) : (
        <Loader2 className="h-6 w-6 animate-spin" />
      )}
    </div>
  );
}

function SettingsPage() {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-[#C9A227]/20 bg-white p-6">
        <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
          Admin account settings
        </h2>
        <p className="mt-3 text-sm text-gray-600">
          Change your own password or request an email change. Email changes are
          completed only after the new address is verified.
        </p>
      </div>
      <AccountSecurityForms loginPath={pageRoutes.AdminLogin} />
    </div>
  );
}

export default function AdminDashboard() {
  const location = useLocation();
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const content = useMemo(() => {
    if (location.pathname === pageRoutes.AdminDashboard) return <SummaryCards />;
    if (location.pathname === pageRoutes.AdminMembers) return <MembersPage />;
    if (location.pathname.startsWith("/admin/members/")) return <MemberDetailPage />;
    if (location.pathname === pageRoutes.AdminPayments) return <PaymentsPage />;
    if (location.pathname === pageRoutes.AdminSettings) return <SettingsPage />;
    return <Navigate to={pageRoutes.AdminDashboard} replace />;
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    memberApi("/api/auth/me")
      .then((payload) => {
        if (cancelled) return;
        if (!adminRoles.has(payload.user?.role)) {
          setStatus("forbidden");
          return;
        }
        setUser(payload.user);
        setStatus("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        if (/authentication required/i.test(error.message)) {
          setStatus("unauthenticated");
          return;
        }
        setErrorMessage(error.message);
        setStatus("error");
      });
    return () => { cancelled = true; };
  }, []);

  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-[#FDF8F3]"><Loader2 className="h-8 w-8 animate-spin text-[#722F37]" /></div>;
  }
  if (status === "unauthenticated") {
    return <Navigate to={pageRoutes.AdminLogin} replace />;
  }
  if (status === "forbidden") {
    return (
      <div className="min-h-screen bg-[#FDF8F3] px-4 py-16">
        <div className="mx-auto max-w-xl rounded-xl border border-red-200 bg-white p-8 text-red-900">
          <AlertCircle className="mb-3 h-8 w-8" />
          Admin access is required.
        </div>
      </div>
    );
  }
  if (status === "error") {
    return <div className="p-8 text-red-900">{errorMessage}</div>;
  }
  return <AdminShell user={user}>{content}</AdminShell>;
}
